import { useEffect, useRef, useState } from "react";

/**
 * The Institute's attendance certificates — four approved designs, drawn here
 * rather than shipped as flat artwork.
 *
 * The designs arrived as finished images with the event name set into them:
 * "NATIONAL WORKSHOP", "MANDATORY REFRESHER COURSE (MRC)" and so on. That is
 * exactly the part that has to change per event, so the artwork is rebuilt in
 * CSS and SVG and the title, theme, year and recipient are live text. Nothing
 * on the sheet is an image except the Institute's own lockup, which is the one
 * element that must never be redrawn by hand.
 *
 * One definition, printed and previewed: `certificateDocument()` builds the
 * whole page, `printCertificate()` opens it in a print window, and
 * `CertificatePreview` shows the SAME html in a scaled iframe. The desk, the
 * member and the admin choosing a format on the event form are therefore all
 * looking at the sheet that will actually come out of the printer.
 *
 * Sized in millimetres against A4 landscape throughout, so what the browser
 * lays out is what the paper gets — a certificate laid out in pixels and
 * scaled at print time is a certificate whose margins move per printer.
 */

export type CertificateTemplateKey = "blueprint" | "chevron" | "ribbon" | "ribbon_triple";

/** The API's certificate payload (GET /my/events/{id}/certificate, or the admin twin). */
export type CertificateData = {
  template?: CertificateTemplateKey | string | null;
  template_name?: string | null;
  /** Printed left to right; its length is how many blocks the foot draws. */
  signatures?: string[] | null;
  certificate_no?: string | null;
  recipient_name?: string | null;
  membership_no?: string | null;
  registration_no?: string | null;
  event_title?: string | null;
  event_theme?: string | null;
  event_year?: number | string | null;
  event_location?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  cpd_points?: number | string | null;
  issued_on?: string | null;
};

// ── Text helpers ─────────────────────────────────────────────────────────────

// Escapes into the print window's HTML. Event titles, themes and venues are
// free text typed by admins, and names are typed by members; without this an
// ampersand in "Biennial Conference & General Meeting" or an apostrophe in a
// surname silently breaks the sheet it is printed on.
const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "";

/**
 * "12 – 15 November 2026" where the run spans days, one date where it does not.
 * The month is printed once when both ends share it, which is how the dates
 * read on every programme the Institute puts out.
 */
function dateRange(start?: string | null, end?: string | null): string {
  if (!start) return fmtDate(end);
  if (!end || end === start) return fmtDate(start);

  const a = new Date(start);
  const b = new Date(end);
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

  return sameMonth
    ? `${a.getDate()} – ${fmtDate(end)}`
    : `${fmtDate(start)} – ${fmtDate(end)}`;
}

/**
 * The title as it prints: upper case, with the year appended unless the title
 * already carries it. "2026 Annual Conference" must not become "2026 ANNUAL
 * CONFERENCE 2026".
 */
function headlineTitle(c: CertificateData): string {
  const title = (c.event_title ?? "").trim();
  const year = c.event_year != null && c.event_year !== "" ? String(c.event_year) : "";

  if (!year || title.includes(year)) return title.toUpperCase();
  return `${title} ${year}`.toUpperCase();
}

// ── Seal ─────────────────────────────────────────────────────────────────────

/**
 * The Institute's red seal, as a starburst disc.
 *
 * Built from a generated path rather than a wall of coordinates so the tooth
 * count can be changed in one number. Drawn rather than cropped out of the
 * supplied artwork because the sheets it sits on are blue, white and cream —
 * a cropped seal would carry its own background onto three of the four.
 */
function sealSvg(): string {
  const teeth = 44;
  const outer = 48;
  const inner = 43;
  const pts: string[] = [];

  for (let i = 0; i < teeth * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / teeth - Math.PI / 2;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`);
  }

  return `<svg class="seal" viewBox="0 0 100 100" aria-hidden="true">
  <polygon points="${pts.join(" ")}" fill="#d2232a"/>
  <circle cx="50" cy="50" r="40" fill="#b8171f"/>
  <circle cx="50" cy="50" r="34" fill="none" stroke="#f0a9ac" stroke-width="0.9" opacity=".65"/>
  <circle cx="50" cy="50" r="31" fill="#8e1016"/>
  <g fill="#f3c9cb" font-family="Georgia,'Times New Roman',serif" font-size="9.5" font-weight="700" text-anchor="middle">
    <text x="50" y="34">N</text>
    <text x="50" y="45">I</text>
    <text x="50" y="56">Q</text>
    <text x="50" y="67">S</text>
  </g>
  <circle cx="50" cy="50" r="31" fill="none" stroke="#f0a9ac" stroke-width="0.7" opacity=".5"/>
</svg>`;
}

// ── Template chrome ──────────────────────────────────────────────────────────

/**
 * The decorative layers behind the text, per design. Everything here is
 * absolutely positioned inside the sheet and sits under `.content`.
 */
function chrome(template: string): string {
  switch (template) {
    // Pale blue over construction line-work, gold and navy corner rules.
    case "blueprint":
      return `
  <div class="bp-lines"></div>
  <svg class="bp-draw" viewBox="0 0 297 210" preserveAspectRatio="none" aria-hidden="true">
    <g stroke="#9fb4e2" stroke-width="0.3" fill="none" opacity=".55">
      <path d="M8 210 L8 96 L52 74 L52 210 Z"/>
      <path d="M52 74 L96 92 L96 210 L52 210 Z"/>
      <path d="M96 92 L140 66 L140 210 L96 210 Z"/>
      <path d="M236 210 L236 58 L278 42 L278 210 Z"/>
      <path d="M190 210 L190 110 L236 92"/>
      ${Array.from({ length: 11 }, (_, i) => `<path d="M8 ${100 + i * 10} L140 ${100 + i * 10}"/>`).join("")}
      ${Array.from({ length: 8 }, (_, i) => `<path d="M236 ${66 + i * 18} L278 ${56 + i * 18}"/>`).join("")}
      ${Array.from({ length: 6 }, (_, i) => `<path d="M${20 + i * 20} 96 L${20 + i * 20} 210"/>`).join("")}
    </g>
  </svg>
  ${["tl", "tr", "bl", "br"].map((k) => `<div class="corner gold ${k}"></div><div class="corner navy ${k}"></div>`).join("")}`;

    // White, framed by navy and gold chevrons down both edges.
    case "chevron":
      return `
  <div class="cv navy left"></div>
  <div class="cv gold left"></div>
  <div class="cv navy right"></div>
  <div class="cv gold right"></div>
  <div class="cv-frame"></div>
  <svg class="cv-waves" viewBox="0 0 297 210" preserveAspectRatio="none" aria-hidden="true">
    <g stroke="#d9bd7e" stroke-width="0.25" fill="none" opacity=".5">
      ${Array.from({ length: 9 }, (_, i) => `<path d="M40 ${150 + i * 6} Q 148 ${118 + i * 6} 257 ${150 + i * 6}"/>`).join("")}
    </g>
  </svg>`;

    // Cream, gold border, navy corner swoop and a navy ribbon banner.
    case "ribbon":
    case "ribbon_triple":
      return `
  <div class="rb-hatch"></div>
  <div class="rb-border"></div>
  <div class="rb-swoop"></div>
  <div class="rb-inner"></div>`;

    default:
      return "";
  }
}

/** The heading block, which is where the four designs differ most. */
function heading(template: string): string {
  switch (template) {
    case "chevron":
      return `
    <div class="hd-chevron">
      <div class="hd-main">CERTIFICATE</div>
      <div class="hd-rule"></div>
      <div class="hd-sub">OF ATTENDANCE</div>
    </div>`;

    case "ribbon":
    case "ribbon_triple":
      return `
    <div class="hd-ribbon"><span class="banner">Certificate of Attendance</span></div>`;

    case "blueprint":
    default:
      return `<div class="hd-script">Certificate of Attendance</div>`;
  }
}

// ── Stylesheet ───────────────────────────────────────────────────────────────

const STYLES = `
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4 landscape;margin:0}
  html,body{width:297mm;height:210mm}
  body{background:#e5e7eb;display:flex;align-items:center;justify-content:center;
       font-family:'Poppins','Segoe UI',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}

  .sheet{position:relative;width:297mm;height:210mm;overflow:hidden;background:#fff;flex:none}

  /* ── Content, common to all four ─────────────────────────────────────── */
  .content{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;
           padding:17mm 30mm 13mm;text-align:center;z-index:3}
  .lockup{height:21mm;object-fit:contain;flex:none}
  .mark{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;gap:0}

  .certify{font-size:3.6mm;font-weight:500;letter-spacing:.06em;color:#1d1d1f;text-transform:uppercase;margin-top:1mm}

  .name-line{width:100%;margin:7mm 0 6mm;padding-bottom:1.6mm}
  .name{display:block;font-size:9mm;line-height:1.1;font-weight:600;color:#101c74;
        font-family:'Cormorant Garamond',Georgia,serif;min-height:10mm}
  .name-line.dotted{border-bottom:0.7mm dotted #1b2a5e}
  .name-line.solid{border-bottom:0.35mm solid #1d1d1f}

  .lead{font-size:3.7mm;line-height:1.65;font-weight:500;color:#1d1d1f;text-transform:uppercase;letter-spacing:.02em}
  .title{font-size:11mm;line-height:1.08;font-weight:800;color:#1a1aa8;letter-spacing:-.01em;margin-top:3mm;
         text-transform:uppercase;max-width:100%;overflow-wrap:break-word}
  /* Long names of events wrap rather than overflow; the two-line case is why
     the block above it is a flexible middle and not a fixed offset. */
  .title.long{font-size:8mm}
  .title.xlong{font-size:6.4mm}
  .theme{font-size:4.2mm;line-height:1.45;font-style:italic;color:#3a3a46;margin-top:3mm;max-width:215mm}
  .meta{font-size:3.2mm;font-weight:600;color:#5a5a66;letter-spacing:.04em;margin-top:3.5mm}

  /* ── Foot: seal and signatures ───────────────────────────────────────── */
  .footer{width:100%;flex:none}
  /* The gap is what the seal sits in. Two blocks are pushed apart to clear
     it; three fill the middle, so the seal moves to the left instead. */
  .foot{position:relative;width:100%;display:flex;align-items:flex-end;justify-content:center;gap:28mm}
  /* Wide enough for "Professional Development & Library" on one line — the
     longest label any of the four designs carries. */
  .sig{flex:0 1 66mm;min-width:0;border-top:0.4mm solid #1b2a5e;padding-top:1.8mm;
       font-size:3.2mm;font-weight:700;color:#101c74;letter-spacing:.01em}
  .seal{position:absolute;bottom:3mm;left:50%;transform:translateX(-50%);width:21mm;height:21mm;z-index:4}
  .foot.triple{gap:10mm}
  .foot.triple .sig{flex:0 1 52mm;font-size:2.9mm}
  .foot.triple .seal{left:0;transform:none}

  /* In the flow under the signatures rather than pinned to the sheet corner:
     every one of the four designs has a frame, a border or a corner rule
     sitting in that corner. */
  .serial{margin-top:3.5mm;text-align:center;font-size:2.5mm;font-weight:700;
          letter-spacing:.14em;color:#9aa0b4}

  /* The faint house mark, right of centre, as on the approved artwork.
     The only logo file is the horizontal lockup, so the window crops it to
     the eagle and roundel at its left — blowing up the whole lockup would
     lay the words NIGERIAN INSTITUTE OF QUANTITY SURVEYORS across the middle
     of the sheet, over the recipient's own name. */
  .watermark{position:absolute;top:50%;left:63%;transform:translate(-50%,-48%);
             width:68mm;height:84mm;overflow:hidden;opacity:.06;z-index:1;pointer-events:none}
  .watermark img{position:absolute;top:50%;left:0;transform:translateY(-50%);width:316mm;max-width:none}

  /* ══ Blueprint ═══════════════════════════════════════════════════════ */
  .tpl-blueprint{background:#e9effc}
  .tpl-blueprint .bp-lines{position:absolute;inset:0;z-index:0;
    background:repeating-linear-gradient(90deg,rgba(150,173,219,.18) 0 0.25mm,transparent 0.25mm 9mm),
               repeating-linear-gradient(0deg,rgba(150,173,219,.14) 0 0.25mm,transparent 0.25mm 9mm)}
  .tpl-blueprint .bp-draw{position:absolute;inset:0;width:100%;height:100%;z-index:1}
  .tpl-blueprint .watermark{opacity:.085}
  .tpl-blueprint .corner{position:absolute;z-index:2}
  .tpl-blueprint .corner.gold{width:34mm;height:28mm;border-color:#cfa84e;border-style:solid;border-width:0}
  .tpl-blueprint .corner.navy{width:26mm;height:21mm;border-color:#101c74;border-style:solid;border-width:0}
  .tpl-blueprint .corner.gold.tl{top:9mm;left:9mm;border-top-width:2.2mm;border-left-width:2.2mm}
  .tpl-blueprint .corner.gold.tr{top:9mm;right:9mm;border-top-width:2.2mm;border-right-width:2.2mm}
  .tpl-blueprint .corner.gold.bl{bottom:9mm;left:9mm;border-bottom-width:2.2mm;border-left-width:2.2mm}
  .tpl-blueprint .corner.gold.br{bottom:9mm;right:9mm;border-bottom-width:2.2mm;border-right-width:2.2mm}
  .tpl-blueprint .corner.navy.tl{top:13mm;left:13mm;border-top-width:0.8mm;border-left-width:0.8mm}
  .tpl-blueprint .corner.navy.tr{top:13mm;right:13mm;border-top-width:0.8mm;border-right-width:0.8mm}
  .tpl-blueprint .corner.navy.bl{bottom:13mm;left:13mm;border-bottom-width:0.8mm;border-left-width:0.8mm}
  .tpl-blueprint .corner.navy.br{bottom:13mm;right:13mm;border-bottom-width:0.8mm;border-right-width:0.8mm}
  .hd-script{font-family:'Pinyon Script','Snell Roundhand','Apple Chancery',cursive;
             font-size:19mm;line-height:1.15;color:#0d0d12;margin-top:4mm;padding:0 4mm}

  /* ══ Chevron ═════════════════════════════════════════════════════════ */
  .tpl-chevron{background:#fff}
  .tpl-chevron .cv{position:absolute;top:0;height:100%;width:40mm;z-index:1}
  .tpl-chevron .cv.navy{background:#101c74}
  .tpl-chevron .cv.gold{background:#cfa84e}
  .tpl-chevron .cv.left{left:0}
  .tpl-chevron .cv.right{right:0}
  .tpl-chevron .cv.navy.left{clip-path:polygon(0 0,16mm 0,40mm 105mm,16mm 210mm,0 210mm,24mm 105mm)}
  .tpl-chevron .cv.gold.left{clip-path:polygon(0 0,7mm 0,31mm 105mm,7mm 210mm,0 210mm,15mm 105mm);opacity:.95}
  .tpl-chevron .cv.navy.right{clip-path:polygon(40mm 0,24mm 0,0 105mm,24mm 210mm,40mm 210mm,16mm 105mm)}
  .tpl-chevron .cv.gold.right{clip-path:polygon(40mm 0,33mm 0,9mm 105mm,33mm 210mm,40mm 210mm,25mm 105mm);opacity:.95}
  .tpl-chevron .cv-frame{position:absolute;inset:7mm;border:1.1mm solid #cfa84e;z-index:2}
  .tpl-chevron .cv-frame::after{content:"";position:absolute;inset:1.4mm;border:0.3mm solid #cfa84e}
  .tpl-chevron .cv-waves{position:absolute;inset:0;width:100%;height:100%;z-index:1}
  .tpl-chevron .content{padding-left:46mm;padding-right:46mm}
  .hd-chevron{margin-top:5mm}
  .hd-chevron .hd-main{font-family:'Cormorant Garamond',Georgia,serif;font-size:15mm;line-height:1;
                       font-weight:400;letter-spacing:.17em;color:#101c74;text-indent:.17em}
  .hd-chevron .hd-rule{width:86mm;height:0.3mm;background:#cfa84e;margin:3.5mm auto 3mm}
  .hd-chevron .hd-sub{font-family:'Cormorant Garamond',Georgia,serif;font-size:5.4mm;
                      font-weight:600;letter-spacing:.42em;color:#1d1d1f;text-indent:.42em}

  /* ══ Ribbon (and its three-signature twin) ═══════════════════════════ */
  .tpl-ribbon,.tpl-ribbon_triple{background:#fdf8e9}
  .rb-hatch{position:absolute;inset:0;z-index:0;
    background:repeating-linear-gradient(45deg,rgba(207,168,78,.16) 0 0.3mm,transparent 0.3mm 2.2mm)}
  .rb-border{position:absolute;inset:0;border:9mm solid #e7c475;z-index:1}
  .rb-border::after{content:"";position:absolute;inset:-9mm;border:1.6mm solid #cfa84e}
  /* The navy quarter-round that cuts across the top-left corner, over the
     gold border, with a gold arc following its edge. */
  .rb-swoop{position:absolute;top:0;left:0;width:62mm;height:48mm;background:#101c74;
            border-bottom-right-radius:62mm 48mm;z-index:2}
  .rb-swoop::after{content:"";position:absolute;inset:0;border-bottom-right-radius:62mm 48mm;
                   border-right:0.8mm solid #e7c475;border-bottom:0.8mm solid #e7c475}
  .rb-inner{position:absolute;inset:13mm;border:0.9mm solid #101c74;z-index:2}
  .rb-inner::after{content:"";position:absolute;inset:1.1mm;border:0.3mm solid #101c74}
  .tpl-ribbon .content,.tpl-ribbon_triple .content{padding:19mm 34mm 16mm}
  .hd-ribbon{position:relative;display:flex;justify-content:center;margin-top:4mm}
  .hd-ribbon .banner{display:flex;align-items:center;justify-content:center;background:#101c74;color:#fff;
                     font-family:'UnifrakturMaguntia','Old English Text MT',Georgia,serif;
                     font-size:13mm;line-height:1;padding:5mm 16mm 6mm;
                     clip-path:polygon(0 0,100% 0,calc(100% - 8mm) 100%,8mm 100%)}
  .tpl-ribbon .title,.tpl-ribbon_triple .title{color:#1a1aa8}
`;

// ── Document ─────────────────────────────────────────────────────────────────

/**
 * The whole printable page for one certificate.
 *
 * `origin` is where the Institute lockup is fetched from. It is passed in
 * rather than read here so the preview iframe and the print window, which have
 * different documents but the same host, resolve it identically.
 */
export function certificateDocument(c: CertificateData, opts?: { origin?: string; autoPrint?: boolean }): string {
  const origin = opts?.origin ?? window.location.origin;
  const template = String(c.template ?? "blueprint");
  const signatures = (c.signatures ?? []).filter(Boolean);
  const triple = signatures.length >= 3;

  const title = headlineTitle(c);
  // The headline is the one block that can be any length. Three sizes rather
  // than auto-shrinking script: a measured fit would have to run after layout,
  // and the print window prints before it could.
  const titleClass = title.length > 58 ? "title xlong" : title.length > 34 ? "title long" : "title";

  const dates = dateRange(c.event_start_date, c.event_end_date);
  const cpd = c.cpd_points != null && c.cpd_points !== "" ? `${Number(c.cpd_points)} CPD points` : "";
  const meta = [dates, c.event_location, cpd].filter(Boolean).map(esc).join(" &nbsp;·&nbsp; ");

  // The dotted rule belongs to the blueprint sheet; the rest rule solid.
  const nameRule = template === "blueprint" ? "dotted" : "solid";

  // Autoprint waits for the webfonts. Printing on `load` alone prints the
  // fallback faces — the script heading would come out as plain serif, which
  // is the single most visible thing on the sheet.
  const printScript = opts?.autoPrint === false ? "" : `
  <script>
    (function () {
      var fired = false;
      var go = function () { if (fired) return; fired = true; window.focus(); window.print(); };
      // Cap the wait: offline, Google Fonts never settles and the member is
      // left looking at a page with no print dialog and no explanation.
      setTimeout(go, 2500);
      if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { setTimeout(go, 250); }); }
      else { window.addEventListener('load', go); }
    })();
  </script>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Certificate${c.recipient_name ? ` — ${esc(c.recipient_name)}` : ""}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700&family=Pinyon+Script&family=Poppins:wght@400;500;600;700;800&family=UnifrakturMaguntia&display=swap">
<style>${STYLES}</style>
</head>
<body>
<div class="sheet tpl-${esc(template)}">
  ${chrome(template)}
  <div class="watermark"><img src="${esc(origin)}/niqs-logo.png" alt="" onerror="this.closest('.watermark').remove()"></div>

  <div class="content">
    <img class="lockup" src="${esc(origin)}/niqs-logo.png" alt="Nigerian Institute of Quantity Surveyors" onerror="this.remove()">
    ${heading(template)}

    <div class="mark">
      <div class="certify">This is to certify that</div>

      <div class="name-line ${nameRule}">
        <span class="name">${esc(c.recipient_name)}</span>
      </div>

      <div class="lead">
        Attended and fully participated in the<br>
        Nigerian Institute of Quantity Surveyors (NIQS)
      </div>

      <div class="${titleClass}">${esc(title)}</div>
      ${c.event_theme ? `<div class="theme">&ldquo;${esc(c.event_theme)}&rdquo;</div>` : ""}
      ${meta ? `<div class="meta">${meta}</div>` : ""}
    </div>

    <div class="footer">
      <div class="foot${triple ? " triple" : ""}">
        ${sealSvg()}
        ${signatures.map((s) => `<div class="sig">${esc(s)}</div>`).join("")}
      </div>
      ${c.certificate_no ? `<div class="serial">${esc(c.certificate_no)}</div>` : ""}
    </div>
  </div>
</div>${printScript}
</body>
</html>`;
}

/** Opens the certificate in a print window. */
export function printCertificate(c: CertificateData) {
  const w = window.open("", "_blank", "width=1180,height=860");
  if (!w) return;
  w.document.write(certificateDocument(c));
  w.document.close();
}

// ── Preview ──────────────────────────────────────────────────────────────────

/**
 * The same document, scaled into the page.
 *
 * An iframe rather than a React re-implementation, so the preview cannot drift
 * from the print. The sheet is laid out at its true A4 landscape size and
 * scaled down by measurement — `zoom` would have been shorter and is not a
 * thing outside Chromium.
 */
export function CertificatePreview({ data, className = "" }: { data: CertificateData; className?: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  // A4 landscape at 96dpi — the pixel size the iframe's own mm layout comes to.
  const W = 1123;
  const H = 794;

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const fit = () => setScale(el.clientWidth / W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={boxRef} className={`relative w-full overflow-hidden ${className}`} style={{ height: scale ? H * scale : 0 }}>
      <iframe
        title="Certificate preview"
        // autoPrint off: a preview that opened the print dialog by itself would
        // be unusable on the event form, where the admin is comparing designs.
        srcDoc={certificateDocument(data, { autoPrint: false })}
        sandbox="allow-same-origin"
        scrolling="no"
        style={{
          width: W,
          height: H,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ── Sample ───────────────────────────────────────────────────────────────────

/**
 * Stand-in content for previewing a design before anything has been issued —
 * the event form, where the admin is choosing a format and there is no
 * recipient yet. Takes whatever of the real event is already typed in.
 */
export function sampleCertificate(
  template: string,
  signatures: string[],
  event?: { title?: string; theme?: string; year?: string | number; location?: string; start_date?: string; end_date?: string; cpd_points?: string | number },
): CertificateData {
  const year = event?.year || new Date().getFullYear();

  return {
    template,
    signatures,
    certificate_no: `NIQS/CERT/${year}/00000`,
    recipient_name: "Qs. Adaeze N. Okonkwo",
    event_title: event?.title?.trim() || "National Workshop",
    event_theme: event?.theme?.trim() || undefined,
    event_year: year,
    event_location: event?.location?.trim() || undefined,
    event_start_date: event?.start_date || undefined,
    event_end_date: event?.end_date || undefined,
    cpd_points: event?.cpd_points || undefined,
  };
}
